// src/pages/Profile.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser, UserProfile } from "@clerk/clerk-react";
import {
  ArrowLeft,
  Shield,
  Calendar,
  MapPin,
  Home,
  UserCheck,
  Mail,
  Phone,
} from "lucide-react";

import Navbar from "../components/Navbar";
import { getChurches } from "../services/api"; // ✅ resolve church name from org structure API

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_API_BASE?.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE &&
    process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  "http://localhost:5050";

async function fetchJSON(path, { token } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
  });
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const json = isJson ? await res.json().catch(() => ({})) : {};
  if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
  return json?.data ?? json;
}

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-KE", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const a = parts[0]?.[0] || "";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase() || "U";
}

export default function Profile() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user, isLoaded } = useUser();

  const [me, setMe] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState("overview"); // "overview" | "settings"

  // ✅ churchId -> churchName cache (resolved from org API)
  const [churchNameMap, setChurchNameMap] = useState(null);

  const ensureChurchMap = useCallback(async () => {
    if (churchNameMap) return churchNameMap;
    const list = await getChurches().catch(() => []);
    const m = new Map();
    (Array.isArray(list) ? list : []).forEach((c) => {
      m.set(String(c.id), c.name || "");
    });
    setChurchNameMap(m);
    return m;
  }, [churchNameMap]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setErr("");
        setLoading(true);
        const token = await getToken().catch(() => null);
        const data = await fetchJSON("/api/users/me", { token });
        if (!alive) return;

        setMe(data || {});

        // If user has church_id but no church_name, pre-load church map so UI shows a name fast
        const churchId = data?.church_id ?? data?.scope?.churchId ?? null;
        const alreadyHasName =
          !!(data?.church_name || data?.churchName || data?.church);
        if (churchId && !alreadyHasName) {
          ensureChurchMap().catch(() => {});
        }
      } catch (e) {
        if (alive) setErr(e.message || "Failed to load profile");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [getToken, ensureChurchMap]);

  // --- Normalize backend fields (defensive) ---
  const fullName = useMemo(() => {
    const fromApi =
      me?.full_name ||
      me?.fullName ||
      [me?.first_name, me?.last_name].filter(Boolean).join(" ");
    const fromClerk =
      user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(" ");
    return (fromApi || fromClerk || "").trim();
  }, [me, user]);

  const email = useMemo(() => {
    const fromApi = me?.email || me?.email_address || me?.emailAddress;
    const fromClerk = user?.primaryEmailAddress?.emailAddress;
    return (fromApi || fromClerk || "").trim();
  }, [me, user]);

  const phone = useMemo(() => {
    const fromApi =
      me?.phone ||
      me?.phone_number ||
      me?.phoneNumber ||
      me?.phone_primary ||
      me?.phonePrimary;
    const fromClerk = user?.primaryPhoneNumber?.phoneNumber;
    return (fromApi || fromClerk || "").trim();
  }, [me, user]);

  // ✅ Church name resolution (org-structure API)
  const churchName = useMemo(() => {
    const explicit =
      me?.church_name ||
      me?.churchName ||
      (typeof me?.church === "string" ? me.church : null);

    if (explicit) return String(explicit);

    const churchId = me?.church_id ?? me?.scope?.churchId ?? null;
    if (churchId && churchNameMap) {
      const found = churchNameMap.get(String(churchId));
      if (found) return found;
    }

    // If no church selected / non-SDA path
    if (me?.no_church === true) return "No church / Not SDA";

    // fallback: if we *must* show something, show ID; otherwise blank
    return churchId ? String(churchId) : "";
  }, [me, churchNameMap]);

  const districtName = useMemo(
    () => me?.district_name || me?.districtName || "",
    [me]
  );
  const stationName = useMemo(
    () => me?.station_name || me?.stationName || "",
    [me]
  );
  const conferenceName = useMemo(
    () => me?.conference_name || me?.conference_abbrev || me?.conferenceName || "",
    [me]
  );

  const instructorName = useMemo(() => {
    return (
      me?.instructor_name ||
      me?.instructorName ||
      me?.instructor_full_name ||
      me?.instructor ||
      ""
    );
  }, [me]);

  const instructorPhone = useMemo(() => {
    return me?.instructor_phone || me?.instructorPhone || me?.instructor_contact || "";
  }, [me]);

  const roleLabel = useMemo(() => {
    const r = (me?.role && String(me.role).toLowerCase()) || "learner";
    if (r === "learner") return "Learner";
    return r[0].toUpperCase() + r.slice(1);
  }, [me]);

  const joinedDate = useMemo(() => {
    return formatDate(me?.joined_at || me?.joinedAt || me?.created_at || me?.createdAt || null);
  }, [me]);

  const avatarUrl = user?.imageUrl || "";
  const avatarFallback = initialsFromName(fullName || email || "User");

  const pillBtn = (active) =>
    `px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
      active
        ? "bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow"
        : "bg-white text-slate-700 hover:bg-slate-50 border border-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:border-slate-700"
    }`;

  const InfoCard = ({ icon: Icon, title, children, className = "" }) => (
    <div
      className={`bg-white rounded-2xl border border-orange-100 shadow-sm p-5 dark:bg-slate-950/60 dark:border-slate-800 ${className}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 rounded-xl bg-orange-50 border border-orange-100 dark:bg-orange-500/10 dark:border-orange-500/20">
          <Icon className="text-orange-700 dark:text-orange-200" size={18} />
        </div>
        <h3 className="font-bold text-slate-900 dark:text-white">{title}</h3>
      </div>
      <div className="text-sm text-slate-700 dark:text-slate-200">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-white dark:from-slate-950 dark:via-slate-950 dark:to-slate-900">
      <Navbar />

      <div className="pt-24 pb-12 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="group inline-flex items-center gap-2 text-slate-700 hover:text-orange-700 dark:text-slate-200 dark:hover:text-orange-200"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            Back
          </button>

          <div className="flex items-center gap-2">
            <button className={pillBtn(tab === "overview")} onClick={() => setTab("overview")}>
              Overview
            </button>
            <button className={pillBtn(tab === "settings")} onClick={() => setTab("settings")}>
              Edit & Security
            </button>
          </div>
        </div>

        {/* Error */}
        {!!err && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
            {err}
          </div>
        )}

        {/* Layout */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Profile summary */}
          <aside className="lg:col-span-1">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-orange-100 overflow-hidden dark:bg-slate-950/60 dark:border-slate-800">
              {/* Cover */}
              <div className="bg-gradient-to-r from-orange-600 to-amber-600 p-6">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold uppercase tracking-wide">
                    <Shield size={14} />
                    {roleLabel}
                  </span>
                  {joinedDate && (
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold">
                      <Calendar size={14} />
                      {joinedDate}
                    </span>
                  )}
                </div>
              </div>

              {/* Avatar + identity */}
              <div className="p-6 -mt-10">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-orange-400 to-amber-400 blur opacity-30" />
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt="Profile"
                        className="relative w-16 h-16 rounded-full object-cover border-4 border-white shadow"
                      />
                    ) : (
                      <div className="relative w-16 h-16 rounded-full bg-white border-4 border-white shadow flex items-center justify-center">
                        <span className="text-orange-700 font-extrabold">{avatarFallback}</span>
                      </div>
                    )}
                  </div>

                  <div className="min-w-0">
                    <p className="text-lg font-extrabold text-slate-900 dark:text-white truncate">
                      {loading ? "Loading…" : fullName || "Your Profile"}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-300 truncate">
                      {email || "—"}
                    </p>
                  </div>
                </div>

                {/* Church highlight */}
                <div className="mt-5 rounded-2xl border border-orange-100 bg-orange-50 p-4 dark:bg-orange-500/10 dark:border-orange-500/20">
                  <div className="flex items-start gap-3">
                    <Home className="text-orange-700 dark:text-orange-200 mt-0.5" size={18} />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-orange-800 dark:text-orange-200 uppercase tracking-wide">
                        Church
                      </p>
                      <p className="text-slate-900 dark:text-white font-bold break-words">
                        {churchName || "—"}
                      </p>
                      {(districtName || stationName || conferenceName) && (
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 flex items-center gap-1">
                          <MapPin size={12} />
                          {[districtName, stationName, conferenceName].filter(Boolean).join(" • ")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  Use <span className="font-semibold">Edit & Security</span> to update your name,
                  email/phone and change your password.
                </div>
              </div>
            </div>
          </aside>

          {/* Right: Content */}
          <main className="lg:col-span-2">
            {/* Loading */}
            {loading && !err && (
              <div className="bg-white rounded-2xl shadow-lg border border-orange-100 p-10 flex items-center justify-center dark:bg-slate-950/60 dark:border-slate-800">
                <div className="flex flex-col items-center gap-3">
                  <div className="relative">
                    <div className="h-10 w-10 rounded-full border-4 border-orange-200 animate-spin" />
                    <div className="absolute inset-0 h-10 w-10 rounded-full border-t-4 border-orange-600 animate-spin" />
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">Loading your profile…</p>
                </div>
              </div>
            )}

            {!loading && tab === "overview" && (
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <InfoCard icon={Mail} title="Email">
                    <div className="font-semibold text-slate-900 dark:text-white break-words">
                      {email || "—"}
                    </div>
                  </InfoCard>

                  <InfoCard icon={Phone} title="Phone">
                    <div className="font-semibold text-slate-900 dark:text-white break-words">
                      {phone || "—"}
                    </div>
                  </InfoCard>

                  <InfoCard icon={Home} title="Church & SDA Structure" className="sm:col-span-2">
                    <div className="font-extrabold text-slate-900 dark:text-white break-words">
                      {churchName || "—"}
                    </div>
                    {(districtName || stationName || conferenceName) ? (
                      <div className="mt-1 text-slate-600 dark:text-slate-300 flex items-center gap-2 flex-wrap">
                        <MapPin size={14} className="text-orange-700 dark:text-orange-200" />
                        <span>{[districtName, stationName, conferenceName].filter(Boolean).join(" • ")}</span>
                      </div>
                    ) : (
                      <div className="mt-1 text-slate-600 dark:text-slate-300">—</div>
                    )}
                  </InfoCard>

                  <InfoCard icon={UserCheck} title="Assigned Instructor" className="sm:col-span-2">
                    <div className="font-bold text-slate-900 dark:text-white break-words">
                      {instructorName || "Will be assigned by your church / station"}
                    </div>
                    {instructorPhone ? (
                      <div className="mt-1 text-slate-600 dark:text-slate-300">
                        Contact: {instructorPhone}
                      </div>
                    ) : null}
                  </InfoCard>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-orange-100 p-6 dark:bg-slate-950/60 dark:border-slate-800">
                  <h3 className="text-lg font-extrabold text-slate-900 dark:text-white mb-2">
                    Need to change your account details?
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Go to <span className="font-semibold">Edit & Security</span> to update your
                    name/email/phone and change your password securely via Clerk.
                  </p>
                </div>
              </div>
            )}

            {!loading && tab === "settings" && (
              <div className="bg-white rounded-2xl shadow-xl border border-orange-100 overflow-hidden dark:bg-slate-950/60 dark:border-slate-800">
                <div className="p-6 border-b border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-500/10 dark:to-slate-900 dark:border-slate-800">
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    Edit Profile & Security
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                    Update your personal details and password (powered by Clerk).
                  </p>
                </div>

                <div className="p-4 sm:p-6">
                  <UserProfile
                    routing="hash"
                    appearance={{
                      variables: {
                        colorPrimary: "#ea580c",
                        borderRadius: "14px",
                        fontFamily:
                          "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial",
                      },
                      elements: {
                        card: "shadow-none border border-orange-100 rounded-2xl",
                        navbar: "bg-white",
                        pageScrollBox: "bg-white",
                        headerTitle: "text-gray-900",
                        headerSubtitle: "text-gray-600",
                        formButtonPrimary:
                          "bg-gradient-to-r from-orange-600 to-amber-600 hover:opacity-95",
                      },
                    }}
                  />
                </div>
              </div>
            )}

            {!loading && !err && !isLoaded && (
              <div className="mt-6 text-sm text-slate-600 dark:text-slate-300">
                Loading account details…
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
